import Skeleton from "react-loading-skeleton";
import GridWrapper from "../../../components/GridWrapper";
import { IconButton } from "../../../components/buttons";
import { FormLabel } from "../../../components/forms";
import { ErrorMessage, ErrorMessageWrapper } from "../../../components/messages";
import useClientAccounts from '../hooks/useClientAccounts';
import useClientVulnerabilities from "../hooks/useClientVulnerabilities";
import ClientVulnerabilitiesRow from "./ClientVulnerabilitiesRow";
import { openAccountVulnerabilityStatusModal } from "../../../components/modals/client-factfind-vulnerability";
import toast from "react-hot-toast";

const ClientVulnerabilities = ({ vulnerabilitySectionId, clientVulnerabilityIds }) => {
    const {
        data,
        isLoading,
        isError,
        error,
        vulnerabilityCategories,
        vulnerabilityCategoriesLoading,
        vulnerabilityCategoriesError,
        vulnerabilityTypeByCategory,
        vulnerabilityTypesLoading,
        vulnerabilityTypesError,
        refetchClientVulnerabilities,
        createClientVulnerability,
        patchClientVulnerability,
        removeClientVulnerability,
        masterAccountId,
        updateVulnerableAccountStatus
    } = useClientVulnerabilities(vulnerabilitySectionId, clientVulnerabilityIds);

    const { data: clientOptions, isLoading: clientLoading, isError: clientError } = useClientAccounts({ fetchType: 'list' });
    const isDataLoading = isLoading || vulnerabilityCategoriesLoading || vulnerabilityTypesLoading || clientLoading;

    if (isError) {
        return <ErrorMessageWrapper>
            <ErrorMessage error={error} retryCallback={refetchClientVulnerabilities} />
        </ErrorMessageWrapper>;
    }

    const handleRemove = async (vulnerabilityId) => {
        const isListEmpty = await toast.promise(removeClientVulnerability(vulnerabilityId), {
            loading: `Removing client vulnerability...`,
            success: () => {
                return `The client vulnerability has been removed.`;
            },
            error: `Failed to remove client vulnerability.`
        });

        if (isListEmpty) {
            openAccountVulnerabilityStatusModal(masterAccountId)
                .then(
                    (proceed) => {
                        if (proceed) {
                            toast.promise(updateVulnerableAccountStatus(false), {
                                loading: `Removing vulnerable client status from master account...`,
                                success: () => {
                                    return `The vulnerable client status has been removed from this master account.`;
                                },
                                error: `Failed to remove the vulnerable client status.`
                            });
                        }
                    })
        }
    }

    const handleCreate = async () => {
        const createVulnerability = await toast.promise(createClientVulnerability(), {
            loading: `Adding client vulnerability...`,
            success: () => {
                return `The client vulnerability has been added.`;
            },
            error: `Failed to add client vulnerability.`
        });

        if (createVulnerability !== null && clientVulnerabilityIds.length === 0) {
            toast.promise(updateVulnerableAccountStatus(true), {
                loading: `Applying vulnerable client status to master account...`,
                success: () => {
                    return `The vulnerable client status has been applied to this master account.`;
                },
                error: `Failed to apply the vulnerable client status.`
            });
        }

    }

    return <div>
        {isLoading
            ? <Skeleton height={30} count={2} />
            : data.length === 0
            && <p className="mt-2 mb-0">There are no active client vulnerabilities. Click <button className="anchor" onClick={handleCreate}>here</button> to create one</p>}
        {data && data?.length > 0 &&
            <>
                <div className="d-flex row justify-content-between align-items-center">
                    <FormLabel className="col-11">
                        Client Vulnerabilities
                    </FormLabel>
                </div>
                <GridWrapper className="d-grid" gridTemplateColumns={"2fr 2fr 2fr 5fr auto"}>
                    {data.length > 0 && <>
                        <div className="col-start-1 d-flex align-items-end">
                            <FormLabel>
                                Category
                            </FormLabel>
                        </div>
                        <div className="col-start-2 d-flex align-items-end">
                            <FormLabel>
                                Type
                            </FormLabel>
                        </div>
                        <div className="col-start-3 d-flex align-items-end">
                            <FormLabel>
                                Client
                            </FormLabel>
                        </div>
                        <div className="col-start-4 d-flex align-items-end">
                            <FormLabel>
                                Additional information and how client is impacted
                            </FormLabel>
                        </div>
                        <div className="col-start-5 d-flex align-items-end">
                            <IconButton
                                icon="plus"
                                variant="success"
                                onClick={handleCreate} />
                        </div>
                    </>}
                    {data.map((clientVulnerability, index) => (
                        isDataLoading || !clientVulnerability?.vulnerabilityId
                            ? (<Skeleton height={30} />)
                            : (<ClientVulnerabilitiesRow
                                key={clientVulnerability?.vulnerabilityId ?? index}
                                data={clientVulnerability}
                                isLoading={isLoading}
                                error={error}
                                vulnerabilityCategories={vulnerabilityCategories}
                                vulnerabilityCategoriesLoading={vulnerabilityCategoriesLoading}
                                vulnerabilityCategoriesError={vulnerabilityCategoriesError}
                                vulnerabilityTypeByCategory={vulnerabilityTypeByCategory}
                                vulnerabilityTypesLoading={vulnerabilityTypesLoading}
                                vulnerabilityTypesError={vulnerabilityTypesError}
                                clientOptions={clientOptions}
                                clientLoading={clientLoading}
                                clientError={clientError}
                                onChange={patchClientVulnerability}
                                onDelete={handleRemove}
                                refetch={refetchClientVulnerabilities} />)
                    ))}
                </GridWrapper>
            </>}
    </div >
};

export default ClientVulnerabilities;